"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQueryPrivateImplementationsMeta } from "@zapier/client";
import { useNamespacedTranslations } from "../useNamespacedTranslations";
import {
  useMultiValueParameter,
  type MultiFilterOperator,
} from "../../../../hooks/useRouterParams";
import type { ActiveChip } from "../types";
import { useRegisterFilter } from "../FilterBarContext";
import type { FilterControllerProps } from "../types";
import { AppFilterMenu } from "./AppFilterMenu";

const ICON = "navApps";

interface Props extends FilterControllerProps {
  /** Operators offered in the match-type picker. Defaults to `["any", "all"]`.
   * Not part of `FilterControllerProps` since only some callers (e.g.
   * Connections) need to override it. */
  availableOperators?: MultiFilterOperator[];
}

export function AppFilterController({
  isOpen,
  namespace,
  onInteraction,
  availableOperators,
}: Props): JSX.Element | null {
  const t = useNamespacedTranslations(namespace);

  const apps = useMultiValueParameter("apps", "appsOperator");

  // Accumulate resolved display names so adding a new app doesn't briefly flash
  // its raw id while the query refetches. Built in a memo rather than mutated
  // during render, and carried across renders via the ref.
  const { implementationsMeta: selectedApps } =
    useQueryPrivateImplementationsMeta({ selectedApis: apps.values });
  const appNameCacheReference = useRef(new Map<string, string>());
  const appNameCache = useMemo(() => {
    const merged = new Map(appNameCacheReference.current);
    for (const app of selectedApps) {
      const versionlessId = (app.id ?? "").replace(/@[\w.-]+$/, "");
      const display = app.name?.replace(/\s*\(\d[\w.-]*\)\s*$/, "").trim();
      if (versionlessId && display) merged.set(versionlessId, display);
    }
    return merged;
  }, [selectedApps]);
  useEffect(() => {
    appNameCacheReference.current = appNameCache;
  }, [appNameCache]);
  const resolveAppName = useCallback(
    (id: string): string => appNameCache.get(id) ?? id,
    [appNameCache],
  );

  const hasValue = apps.values.length > 0;

  let chip: ActiveChip | undefined;
  let signature = "app:none";

  if (hasValue) {
    const first = resolveAppName(apps.values[0]);
    const count = apps.values.length - 1;
    const key = apps.operator === "all" ? "allOf" : "anyOf";
    const label = t(`app.chipLabel.${key}`, { first, count });
    const ariaLabel = t(`app.chipAriaLabel.${key}`, { first, count });
    const tooltip =
      apps.values.length > 1
        ? apps.values.map((id) => resolveAppName(id)).join(", ")
        : undefined;
    chip = {
      key: "app",
      iconName: ICON,
      title: t("app.label"),
      label,
      ariaLabel,
      tooltip,
    };
    // label captures resolved names, so the chip re-renders when an async
    // name lookup resolves id -> display name.
    signature = `app:${apps.values.join(",")}:${apps.operator}:${label}`;
  } else if (isOpen) {
    chip = {
      key: "app",
      iconName: ICON,
      title: t("app.label"),
      ariaLabel: t("app.label"),
      isDraft: true,
    };
    signature = "app:draft";
  }

  useRegisterFilter({
    type: "app",
    hasValue,
    signature,
    chip,
    renderMenu: () => (
      <AppFilterMenu
        namespace={namespace}
        selectedIds={apps.values}
        operator={apps.operator}
        availableOperators={availableOperators}
        onOperatorChange={(op: MultiFilterOperator) => apps.setOperator(op)}
        onToggle={(id: string, pendingOperator?: MultiFilterOperator) => {
          apps.toggle(id, pendingOperator);
          onInteraction?.();
        }}
      />
    ),
  });
  // eslint-disable-next-line unicorn/no-null -- headless controller renders nothing
  return null;
}
